import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CaptionMD, TitleMD } from '@/atoms/Text'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useGuildMember } from '@/services/GuildUserService'
import { useTranslate } from '@/utils/translate/translate-client'
import { getCustomPerkIcon, getExtraPerksForTheatricalSlug, PerkType } from '../utils'

interface Props {
  theatricalSlug: string
  guildMembershipIncluded?: boolean
}

export const ExpressPerks: FC<Props> = ({ theatricalSlug, guildMembershipIncluded = false }) => {
  const { t } = useTranslate('pif')
  const { selectedOffer } = usePayItForwardOfferData()
  const { isDarkMode } = useThemeContext()
  const { isGuildMember } = useGuildMember()

  const perksFromOffer = useMemo(() => selectedOffer?.offeredproductSet, [selectedOffer?.offeredproductSet])

  return (
    <FlexColumn className="gap-4">
      <TitleMD color={isDarkMode ? 'white' : 'core-gray-950'} weight="semibold">
        {t('yourImpactAndPerks', 'Your Impact and Perks')}
      </TitleMD>
      <ul className="relative flex flex-col  gap-3">
        {perksFromOffer?.map((perk) => {
          if (!perk) return null
          return (
            <Perk
              theatricalSlug={theatricalSlug}
              type={perk.product.pif ? 'PIF' : 'default'}
              key={perk.id}
              text={perk.name}
            />
          )
        })}

        {getExtraPerksForTheatricalSlug(theatricalSlug, isGuildMember, guildMembershipIncluded, t).map((perk) => (
          <Perk
            key={perk.id}
            theatricalSlug={theatricalSlug}
            type={perk.type}
            text={perk.text}
            enabled={perk.enabled}
          />
        ))}
      </ul>
    </FlexColumn>
  )
}

export const Perk = ({
  text,
  type,
  enabled = true,
  theatricalSlug,
}: {
  text: string | JSX.Element
  type: PerkType
  theatricalSlug: string
  enabled?: boolean
}) => {
  const { isDarkMode } = useThemeContext()

  return (
    <li className={classNames('flex items-center gap-2', { 'opacity-50': !enabled })}>
      <PerkIcon perkType={type} enabled={enabled} isDarkMode={isDarkMode} theatricalSlug={theatricalSlug} />
      <CaptionMD color={isDarkMode ? 'core-gray-200' : 'core-gray-800'}>{text}</CaptionMD>
    </li>
  )
}

const PerkIcon = ({
  perkType,
  enabled,
  isDarkMode,
}: {
  perkType: PerkType
  enabled: boolean
  isDarkMode: boolean
  theatricalSlug: string
}) => {
  const icon = getCustomPerkIcon(perkType, enabled, isDarkMode)

  if (icon) {
    return icon
  }

  if (enabled) {
    return (
      <CircleCheckIcon
        className="mb-auto shrink-0 leading-[1.125rem] sm:my-0"
        size={24}
        color1={isDarkMode ? 'oxide-bright' : 'oxide-primary'}
        color2={isDarkMode ? 'core-gray-950' : 'white'}
      />
    )
  }

  return (
    <CircleCheckIcon
      className="mb-auto shrink-0 leading-[1.125rem] sm:my-0"
      size={24}
      color1="core-gray-400"
      color2="core-gray-100"
    />
  )
}
